import { captureMessage } from '@sentry/react';
import axiosRetry from 'axios-retry';
import axios from 'axios';

const apiUrl = `${process.env.REACT_APP_API_URL}`.trim();
const appVersion = process.env.REACT_APP_VERSION;

type Token = {
  access_token: string;
  expiresAt: number;
  expires_in: number;
  scope: string;
  token_type: string;
  version: string; // app version when the token was made. Be sure to refresh token when app version changes for security reasons
  apiUrl: string; // api url must be the same also for a valid token
};

const getAuthHeaders = async () => {
  let tokenObject: Token | null;

  // ==> Check for saved token in local storage
  const savedTokenString = localStorage?.getItem('access_token');
  if (savedTokenString) {
    try {
      tokenObject = JSON.parse(savedTokenString);
      console.log(
        `Saved token expires in ${
          (tokenObject.expiresAt - Date.now()) / 1000
        } seconds`
      );
    } catch (e) {
      console.warn(`saved token could not be parsed:${savedTokenString}`, e);
      captureMessage('saved token could not be parsed', e);
    }
  }

  // => Retrieve from api if no saved or expired
  if (
    !tokenObject ||
    !tokenObject.expiresAt ||
    tokenObject.version !== appVersion ||
    tokenObject.apiUrl !== apiUrl ||
    tokenObject.expiresAt < Date.now() + 120000
  ) {
    // console.log(`=> Retrieve token from API`, tokenObject);
    const tokenResponse = await axios.get(`${apiUrl}/getToken`);
    tokenObject = tokenResponse.data;
    // add the version and api url
    tokenObject.version = appVersion;
    tokenObject.apiUrl = apiUrl;
    tokenObject.expiresAt = Date.now() + tokenObject.expires_in * 1000; // expires_in is in seconds
    // save it
    localStorage?.setItem('access_token', JSON.stringify(tokenObject));
  }

  const { access_token: accessToken } = tokenObject;
  return {
    Authorization: `Bearer ${accessToken}`,
    'content-type': 'application/json',
  };
};

export const apiInstance = axios.create({
  baseURL: apiUrl,
});
// 3 retries before really failing
axiosRetry(apiInstance, { retries: 3 });

apiInstance.interceptors.request.use(async (config) => {
  const headers = await getAuthHeaders();
  return { ...config, headers };
});
